import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DistributionCenterTable from 'components/DistributionCenterTable/DistributionCenterTable';
import { CustomModal } from 'components/shared';
import { useTableForm } from 'hooks';
import { DistributionCenter, TransferPalletBalance } from 'interfaces';
import {
   checkIfDefinedSomePalletsToPickUp,
   getDefaultTransferPalletsValues,
} from 'utils/functions';
import { transferPalletsValidations } from 'utils/validation/transferPalletsDistributionCenter';

import styles from './TransferPalletsDistributionCenterModal.module.css';

type TransferPalletsDistributionCenterModalProps = {
   setTransferPallets: (state: boolean) => void;
   isOpened: boolean;
   mode: string;
   distributionCenter: DistributionCenter;
   isLoading: boolean;
   onSubmit: (
      balance: TransferPalletBalance[],
      commercialNetworkId: string,
      setError: (value: string) => void,
   ) => void;
};

const TransferPalletsDistributionCenterModal = ({
   isOpened,
   mode,
   distributionCenter,
   setTransferPallets,
   onSubmit,
   isLoading,
}: TransferPalletsDistributionCenterModalProps) => {
   const { t } = useTranslation();
   const [errorInfo, setErrorInfo] = useState<string>('');

   const admissionSubmit = (values: TransferPalletBalance[]) => {
      onSubmit(values, distributionCenter.commercialNetworkId!, setErrorInfo);
   };

   const {
      rows,
      errors,
      touched,
      handleChangeValue,
      handleSubmitForm,
      setRows,
      hasErrors,
      commonErrorsOfTouchedInputs,
   } = useTableForm<TransferPalletBalance>({
      initialState: getDefaultTransferPalletsValues(),
      onSubmit: admissionSubmit,
      validations: transferPalletsValidations,
   });

   const areDefinedSomePalletsToPickUp = useMemo(
      () => checkIfDefinedSomePalletsToPickUp(rows),
      [rows],
   );

   const isInvalidForm = hasErrors || !areDefinedSomePalletsToPickUp;

   return (
      <>
         <CustomModal
            className={styles.container}
            modalContentClassName={styles.modalContent}
            size={798}
            opened={isOpened}
            primaryButtonProps={{ text: t('distribution_center_details_page.move_to_pickup'), disabled: isInvalidForm }}
            secondaryButtonProps={{
               text: t('common.cancel'),
               onClick: () => {
                  setTransferPallets(false);
                  setRows(getDefaultTransferPalletsValues());
               },
            }}
            onClose={() => {
               setTransferPallets(false);
               setRows(getDefaultTransferPalletsValues());
            }}
            title={t('distribution_center_details_page.move_to_pickup')}
            innerTitle={t('transferModal.subtitle')}
            isLoading={isLoading}
            onSubmit={handleSubmitForm}
            error={errorInfo}
         >
            <div className={styles.modalContent}>
               <DistributionCenterTable
                  mode={mode}
                  handleChangeValue={handleChangeValue}
                  rows={rows}
                  errors={errors}
                  touched={touched}
                  isVisibleErrorBanner={!!commonErrorsOfTouchedInputs.size}
               />
            </div>
         </CustomModal>
      </>
   );
};

export default TransferPalletsDistributionCenterModal;
